import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  idle: { label: "Waiting", ring: "ring-slate-200", glow: "rgba(148,163,184,0)", dot: "bg-slate-300" },
  listening: { label: "Listening…", ring: "ring-cyan-400/60", glow: "rgba(6,182,212,0.45)", dot: "bg-cyan-400" },
  thinking: { label: "Thinking…", ring: "ring-amber-400/60", glow: "rgba(245,158,11,0.4)", dot: "bg-amber-400" },
  speaking: { label: "Speaking", ring: "ring-emerald-400/70", glow: "rgba(16,185,129,0.5)", dot: "bg-emerald-400" },
};

export const frameUrl = (key, frame) => `${API}/api/avatars/${key}/${frame}`;

// Picks which mouth frame to show. With a live audio level we follow the voice;
// without one we run a natural-looking random talking cadence.
function useMouth(talking, level, hasFrames) {
  const [mouth, setMouth] = useState("base");
  const hasLevel = typeof level === "number";
  const silentSince = useRef(null);
  const [meterDead, setMeterDead] = useState(false);

  // If the meter never rises while speaking (no audio device, blocked analyser), fall back to cadence.
  useEffect(() => {
    if (!talking || !hasLevel) { silentSince.current = null; setMeterDead(false); return; }
    if (level > 0.05) { silentSince.current = null; setMeterDead(false); return; }
    if (!silentSince.current) silentSince.current = Date.now();
    const t = setTimeout(() => { if (silentSince.current && Date.now() - silentSince.current > 600) setMeterDead(true); }, 650);
    return () => clearTimeout(t);
  }, [talking, hasLevel, level]);

  const useCadence = !hasLevel || meterDead;
  useEffect(() => {
    if (!talking || !hasFrames) { setMouth("base"); return; }
    if (!useCadence) {
      setMouth(level < 0.12 ? "base" : level < 0.4 ? "mid" : "open");
      return;
    }
    let t;
    const step = () => {
      const r = Math.random();
      setMouth(r < 0.3 ? "base" : r < 0.6 ? "mid" : "open");
      t = setTimeout(step, 90 + Math.random() * 110);
    };
    step();
    return () => clearTimeout(t);
  }, [talking, hasFrames, useCadence, level]);
  return mouth;
}

// Natural blinking: a quick eyes-closed frame every few seconds (double-blink sometimes).
function useBlink(enabled) {
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    if (!enabled) { setClosed(false); return; }
    let t1, t2, t3;
    const schedule = () => {
      t1 = setTimeout(() => {
        setClosed(true);
        t2 = setTimeout(() => {
          setClosed(false);
          if (Math.random() < 0.25) {
            t3 = setTimeout(() => { setClosed(true); setTimeout(() => setClosed(false), 110); }, 160);
          }
          schedule();
        }, 120);
      }, 2200 + Math.random() * 3800);
    };
    schedule();
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [enabled]);
  return closed;
}

// Small nods / head tilts so the avatar feels present. More frequent while listening.
function useNod(state, controls) {
  useEffect(() => {
    if (state === "speaking") return;
    let t;
    const listening = state === "listening";
    const schedule = () => {
      t = setTimeout(async () => {
        const r = Math.random();
        try {
          if (listening || r < 0.6) {
            await controls.start({ y: [0, 3, -1, 0], rotate: [0, 0.4, -0.2, 0], transition: { duration: 0.75, ease: "easeInOut" } });
          } else {
            await controls.start({ rotate: [0, -2.2, -2.2, 0], x: [0, -1, -1, 0], transition: { duration: 1.6, ease: "easeInOut" } });
          }
        } catch (err) { console.warn("[AvatarStage] ignored error", err); }
        schedule();
      }, listening ? 1800 + Math.random() * 2200 : 3500 + Math.random() * 4500);
    };
    schedule();
    return () => clearTimeout(t);
  }, [state, controls]);
}

function Face({ avatar, name, state, level, size, frameKey, frames = [], compact = false }) {
  const s = STATUS[state] || STATUS.idle;
  const active = state !== "idle";
  const talking = state === "speaking";
  const hasFrames = !!frameKey && (frames.includes("open") || frames.includes("mid"));
  const hasBlink = !!frameKey && frames.includes("blink");
  const hasLevel = typeof level === "number";
  const lvl = hasLevel ? Math.min(1, Math.max(0, level)) : 0;
  const mouth = useMouth(talking, level, hasFrames);
  const blink = useBlink(hasBlink);
  const nod = useAnimation();
  useNod(state, nod);
  const bars = compact ? [0, 1, 2, 3] : [0, 1, 2, 3, 4, 5, 6];
  const preloaded = useRef(false);

  const srcFor = useMemo(() => ({
    base: hasFrames ? frameUrl(frameKey, "base") : avatar,
    mid: hasFrames && frames.includes("mid") ? frameUrl(frameKey, "mid") : null,
    open: hasFrames ? frameUrl(frameKey, frames.includes("open") ? "open" : "mid") : null,
    blink: hasBlink ? frameUrl(frameKey, "blink") : null,
  }), [hasFrames, hasBlink, frameKey, frames, avatar]);

  useEffect(() => {
    if ((!hasFrames && !hasBlink) || preloaded.current) return;
    preloaded.current = true;
    Object.values(srcFor).filter(Boolean).forEach((u) => { const i = new Image(); i.src = u; });
  }, [hasFrames, hasBlink, srcFor]);

  const shown = blink && srcFor.blink ? "blink" : mouth === "open" ? "open" : mouth === "mid" && srcFor.mid ? "mid" : "base";

  return (
    <div className="relative shrink-0">
      {active && (
        <motion.span
          className="absolute inset-0 rounded-full"
          animate={{ scale: [1, 1.12, 1], opacity: [0.7, 0, 0.7] }}
          transition={{ duration: talking ? 1.1 : 1.8, repeat: Infinity, ease: "easeInOut" }}
          style={{ boxShadow: `0 0 0 ${compact ? 3 : 6}px ${s.glow}` }}
        />
      )}
      <motion.div
        animate={talking
          ? { rotate: [0, -1.2, 0.8, -0.5, 0], y: [0, -1, 1, 0, 0], scale: [1, 1.015, 1, 1.01, 1] }
          : active ? { scale: [1, 1.02, 1], rotate: 0, y: 0 } : { scale: 1, rotate: 0, y: 0 }}
        transition={{ duration: talking ? 2.6 : 2, repeat: active ? Infinity : 0, ease: "easeInOut" }}
        className={`relative ${size} rounded-full overflow-hidden ${compact ? "ring-2" : "ring-4"} ${s.ring} transition-all duration-500`}
        style={{ boxShadow: active ? `0 0 ${compact ? 16 : 40}px ${s.glow}` : "none" }}
        data-testid={compact ? undefined : "avatar-face"}
        data-mouth={shown}
      >
        {/* Nod/tilt layer; all frames stacked inside so blink + lips stay aligned */}
        <motion.div animate={nod} className="absolute inset-0" style={{ scale: 1.04 }} data-testid={compact ? undefined : "avatar-nod-layer"}>
          <img src={srcFor.base} alt={name} className="absolute inset-0 w-full h-full object-cover object-top"
            style={{ opacity: shown === "base" ? 1 : 0 }} data-testid={compact ? "avatar-strip-image" : "avatar-stage-image"} />
          {srcFor.mid && <img src={srcFor.mid} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover object-top"
            style={{ opacity: shown === "mid" ? 1 : 0 }} data-testid="avatar-frame-mid" />}
          {srcFor.open && <img src={srcFor.open} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover object-top"
            style={{ opacity: shown === "open" ? 1 : 0 }} data-testid="avatar-frame-open" />}
          {srcFor.blink && <img src={srcFor.blink} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover object-top"
            style={{ opacity: shown === "blink" ? 1 : 0 }} data-testid="avatar-frame-blink" />}
        </motion.div>
        {talking && !hasFrames && (
          <motion.img
            src={avatar} alt="" aria-hidden
            className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
            style={{ clipPath: "inset(60% 0 0 0)", originX: 0.5, originY: 0.6, ...(hasLevel ? { scaleY: 1 + lvl * 0.08 } : {}) }}
            animate={hasLevel ? undefined : { scaleY: [1, 1.05, 1.01, 1.06, 1, 1.04, 1] }}
            transition={{ duration: 0.7, repeat: Infinity, ease: "easeInOut" }}
            data-testid="avatar-jaw-layer"
          />
        )}
        {talking && (
          <div className={`absolute bottom-0 inset-x-0 ${compact ? "h-4 pb-1" : "h-10 pb-2.5"} bg-gradient-to-t from-black/50 to-transparent flex items-end justify-center gap-0.5`}>
            {bars.map((i) => (
              <motion.span
                key={i}
                className={`${compact ? "w-0.5" : "w-1"} rounded-full bg-emerald-400`}
                animate={{ height: hasLevel ? 2 + lvl * (compact ? 8 : 14) * (0.6 + ((i * 7) % 5) / 10) : (compact ? [2, 8, 2] : [4, 16, 4]) }}
                transition={hasLevel ? { duration: 0.08 } : { duration: 0.5, repeat: Infinity, delay: i * 0.08, ease: "easeInOut" }}
              />
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}

export function AvatarStage({ name, title, avatar, state = "idle", level, frameKey, frames, subtitle, className = "" }) {
  const s = STATUS[state] || STATUS.idle;
  const active = state !== "idle";
  return (
    <div className={`card-surface rounded-3xl p-5 flex flex-col items-center text-center ${className}`} data-testid="avatar-stage">
      <Face avatar={avatar} name={name} state={state} level={level} frameKey={frameKey} frames={frames} size="w-40 h-40 sm:w-52 sm:h-52" />
      <div className="mt-5">
        <div className="font-semibold text-slate-900 text-lg">{name}</div>
        {title && <div className="text-xs text-slate-500 mt-0.5">{title}</div>}
        {subtitle && <div className="text-[11px] text-emerald-700 mt-1 font-medium" data-testid="avatar-stage-subtitle">{subtitle}</div>}
      </div>
      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200" data-testid="avatar-stage-status">
        <span className={`w-2 h-2 rounded-full ${s.dot} ${active ? "animate-pulse" : ""}`} />
        <span className="text-xs font-mono uppercase tracking-wider text-slate-600">{s.label}</span>
      </div>
    </div>
  );
}

export function AvatarStrip({ name, title, avatar, state = "idle", level, frameKey, frames, className = "" }) {
  const s = STATUS[state] || STATUS.idle;
  const active = state !== "idle";
  return (
    <div className={`flex items-center gap-3 min-w-0 ${className}`} data-testid="avatar-strip">
      <Face avatar={avatar} name={name} state={state} level={level} frameKey={frameKey} frames={frames} size="w-12 h-12" compact />
      <div className="min-w-0">
        <div className="font-semibold text-slate-900 leading-tight truncate">{name}</div>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 min-w-0">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.dot} ${active ? "animate-pulse" : ""}`} />
          <span className="font-mono uppercase tracking-wider shrink-0" data-testid="avatar-strip-status">{s.label}</span>
          {title && <span className="truncate">· {title}</span>}
        </div>
      </div>
    </div>
  );
}
